// -----------------------------------------------------------
// FloraIQ — Aba Regras de Validação
// -----------------------------------------------------------

import { useState } from "react";
import { validationRules } from "@/mocks/quality";
import { SeverityBadge, RuleStatusBadge } from "./QualityStatusBadge";
import ConfigureRuleModal from "./ConfigureRuleModal";

export default function ValidationRulesTable() {
  const [search, setSearch] = useState("");
  const [filterGrupo, setFilterGrupo] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const grupos = Array.from(new Set(validationRules.map((r) => r.grupo)));

  const filtered = validationRules.filter((r) => {
    if (search && !r.nome.toLowerCase().includes(search.toLowerCase()) && !r.codigo.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterGrupo && r.grupo !== filterGrupo) return false;
    if (filterSeverity && r.severidade !== filterSeverity) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por código ou nome da regra..."
            className="w-full border border-background-200/70 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-blue-400 transition-colors"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="border border-background-200/70 rounded-md px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-blue-400 transition-colors"
          value={filterGrupo}
          onChange={(e) => setFilterGrupo(e.target.value)}
        >
          <option value="">Todos os grupos</option>
          {grupos.map((g) => (<option key={g} value={g}>{g}</option>))}
        </select>
        <select
          className="border border-background-200/70 rounded-md px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-blue-400 transition-colors"
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
        >
          <option value="">Todas as severidades</option>
          <option value="crítica">Crítica</option>
          <option value="alta">Alta</option>
          <option value="média">Média</option>
          <option value="baixa">Baixa</option>
          <option value="informativa">Informativa</option>
        </select>
        <button onClick={() => setModalOpen(true)} className="btn-primary text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
          <i className="ri-add-line mr-1"></i> Nova Regra
        </button>
      </div>

      {/* Contador */}
      <p className="text-xs text-foreground-500">{filtered.length} de {validationRules.length} regras</p>

      {/* Tabela */}
      <div className="overflow-x-auto border border-background-200/70 rounded-card">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-background-50 border-b border-background-200/70">
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Código</th>
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Nome</th>
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Grupo</th>
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Severidade</th>
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Status</th>
              <th className="text-right px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Conformes</th>
              <th className="text-right px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Inconformes</th>
              <th className="text-center px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">%</th>
              <th className="text-center px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const pct = r.totalAvaliado > 0 ? Math.round((r.conformes / r.totalAvaliado) * 100) : 0;
              return (
                <tr key={r.codigo} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                  <td className="px-3 py-2.5 font-mono text-foreground-500">{r.codigo}</td>
                  <td className="px-3 py-2.5 text-foreground-700 font-medium max-w-[220px] truncate">{r.nome}</td>
                  <td className="px-3 py-2.5 text-foreground-500">{r.grupo}</td>
                  <td className="px-3 py-2.5"><SeverityBadge severity={r.severidade} /></td>
                  <td className="px-3 py-2.5"><RuleStatusBadge status={r.status} /></td>
                  <td className="px-3 py-2.5 text-right text-green-600 font-medium">{r.conformes.toLocaleString("pt-BR")}</td>
                  <td className="px-3 py-2.5 text-right text-red-600 font-medium">{r.inconformes.toLocaleString("pt-BR")}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${
                      pct >= 90 ? "bg-green-100 text-green-700" : pct >= 80 ? "bg-blue-100 text-blue-700" : pct >= 70 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"
                    }`}>{pct}%</span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <button className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer" title="Editar regra">
                      <i className="ri-settings-3-line text-foreground-500 text-sm"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfigureRuleModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}